import { datadogLogs } from "@datadog/browser-logs";
import { getAlloVersion } from "common/src/config";
import React, { useEffect, useMemo, useState } from "react";
import { useAccount } from "wagmi";
import { useRoundById } from "../../../context/RoundContext";
import { ProjectBanner } from "../../common/ProjectBanner";
import RoundEndedBanner from "../../common/RoundEndedBanner";
import Breadcrumb from "../../common/Breadcrumb";
import { isDirectRound, isInfiniteDate } from "../../api/utils";
import { useCartStorage } from "../../../store";
import { Skeleton, SkeletonText } from "@chakra-ui/react";
import { GrantList } from "../KarmaGrant/GrantList";
import { ImpactList } from "../KarmaGrant/ImpactList";
import { useGap } from "../../api/gap";
import { StatList } from "../OSO/ImpactStats";
import { useOSO } from "../../api/oso";
import { useDataLayer } from "data-layer";
import { DefaultLayout } from "../../common/DefaultLayout";
import { mapApplicationToProject, mapApplicationToRound, useApplication } from "../../projects/hooks/useApplication";
import { PassportWidget } from "../../common/PassportWidget";
import { useProjectDetailsParams } from "./hooks/useProjectDetailsParams";
import { ApplicationFormAnswers, Detail, ProjectDetailsTabs, ProjectLinks, Sidebar, ProjectLogo, StakingBannerAndModal } from "./components";
import { useGetApplicationStakes } from "./hooks/useGetApplicationStakes";
import { useIsStakable } from "./components/StakingBannerAndModal/hooks/useIsStakable";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export default function ViewProjectDetails() {
  var _application$anchorAd, _round$roundMetadata, _round$roundMetadata2, _round$roundMetadata3;
  const [selectedTab, setSelectedTab] = useState(0);
  datadogLogs.logger.info("====> Route: /round/:chainId/:roundId/:applicationId");
  datadogLogs.logger.info(`====> URL: ${window.location.href}`);
  const {
    chainId,
    roundId,
    applicationId: paramApplicationId
  } = useProjectDetailsParams();
  const dataLayer = useDataLayer();
  const {
    address: walletAddress
  } = useAccount();
  let applicationId;

  /// handle URLs where the application ID is ${roundId}-${applicationId}
  if (paramApplicationId.includes("-")) {
    applicationId = paramApplicationId.split("-")[1];
  } else {
    applicationId = paramApplicationId;
  }
  const isStakableRound = useIsStakable({
    chainId: Number(chainId),
    roundId
  });
  const {
    data: application,
    error,
    isLoading
  } = useApplication({
    chainId: Number(chainId),
    roundId,
    applicationId: applicationId
  }, dataLayer);
  const {
    data: totalStaked
  } = useGetApplicationStakes(Number(chainId), Number(roundId), (_application$anchorAd = application === null || application === void 0 ? void 0 : application.anchorAddress) !== null && _application$anchorAd !== void 0 ? _application$anchorAd : "", isStakableRound);
  const {
    round: roundDetails
  } = useRoundById(Number(chainId), roundId);
  const projectToRender = application && mapApplicationToProject(application);
  const round = application && mapApplicationToRound(application);
  round && (round.chainId = Number(chainId));
  const isSybilDefenseEnabled = (round === null || round === void 0 || (_round$roundMetadata = round.roundMetadata) === null || _round$roundMetadata === void 0 || (_round$roundMetadata = _round$roundMetadata.quadraticFundingConfig) === null || _round$roundMetadata === void 0 ? void 0 : _round$roundMetadata.sybilDefense) === true || (round === null || round === void 0 || (_round$roundMetadata2 = round.roundMetadata) === null || _round$roundMetadata2 === void 0 || (_round$roundMetadata2 = _round$roundMetadata2.quadraticFundingConfig) === null || _round$roundMetadata2 === void 0 ? void 0 : _round$roundMetadata2.sybilDefense) !== "none";
  const {
    grants,
    impacts
  } = useGap(projectToRender === null || projectToRender === void 0 ? void 0 : projectToRender.projectRegistryId);
  const {
    stats
  } = useOSO(projectToRender === null || projectToRender === void 0 ? void 0 : projectToRender.projectMetadata.projectGithub);
  const currentTime = new Date();
  const isAfterRoundEndDate = round && (isInfiniteDate(round.roundEndTime) ? false : round && round.roundEndTime <= currentTime);
  const isBeforeRoundStartDate = round && (isInfiniteDate(round.roundStartTime) ? false : round && currentTime < round.roundStartTime);
  const alloVersion = getAlloVersion();
  useEffect(() => {
    if (isAfterRoundEndDate !== undefined && roundId !== null && roundId !== void 0 && roundId.startsWith("0x") && alloVersion === "allo-v2" && !isAfterRoundEndDate) {
      window.location.href = `https://explorer-v1.gitcoin.co${window.location.pathname}${window.location.hash}`;
    }
  }, [roundId, alloVersion, isAfterRoundEndDate]);
  const disableAddToCartButton = alloVersion === "allo-v2" && roundId.startsWith("0x") || isAfterRoundEndDate || isBeforeRoundStartDate;
  const {
    projects,
    add,
    remove
  } = useCartStorage();
  const isAlreadyInCart = projects.some(project => project.grantApplicationId === applicationId && project.chainId === Number(chainId) && project.roundId === roundId);
  const cartProject = projectToRender;
  if (cartProject !== undefined) {
    cartProject.roundId = roundId;
    cartProject.chainId = Number(chainId);
    cartProject.grantApplicationId = applicationId;
  }
  const breadCrumbs = [{
    name: "Explorer Home",
    path: "/"
  }, {
    name: round === null || round === void 0 || (_round$roundMetadata3 = round.roundMetadata) === null || _round$roundMetadata3 === void 0 ? void 0 : _round$roundMetadata3.name,
    path: `/round/${chainId}/${roundId}`
  }, {
    name: "Project Details",
    path: `/round/${chainId}/${roundId}/${applicationId}`
  }];
  const {
    projectMetadata: {
      title,
      description = "",
      bannerImg
    }
  } = projectToRender !== null && projectToRender !== void 0 ? projectToRender : {
    projectMetadata: {}
  };
  const projectDetailsTabs = useMemo(() => [{
    name: "Project details",
    content: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("h3", {
        className: "text-3xl mt-8 mb-4 font-modern-era-medium text-blue-800",
        children: "About"
      }), projectToRender ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Detail, {
          text: description,
          testID: "project-metadata"
        }), /*#__PURE__*/_jsx(ApplicationFormAnswers, {
          answers: projectToRender.grantApplicationFormAnswers,
          round: roundDetails
        })]
      }) : /*#__PURE__*/_jsx(SkeletonText, {})]
    })
  }, {
    name: "Impact Measurement",
    content: /*#__PURE__*/_jsxs(React.Fragment, {
      children: [/*#__PURE__*/_jsx(StatList, {
        stats: stats
      }), /*#__PURE__*/_jsx(GrantList, {
        grants: grants,
        displayKarmaAttribution: grants.length > 0 && impacts.length === 0
      }), /*#__PURE__*/_jsx(ImpactList, {
        impacts: impacts,
        displayKarmaAttribution: impacts.length > 0
      })]
    })
  }], [stats, grants, projectToRender, description, impacts, roundDetails]);
  const handleTabChange = tabIndex => {
    setSelectedTab(tabIndex);
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(DefaultLayout, {
      children: [isAfterRoundEndDate && /*#__PURE__*/_jsx("div", {
        className: "relative top-6",
        children: /*#__PURE__*/_jsx(RoundEndedBanner, {})
      }), /*#__PURE__*/_jsxs("div", {
        className: "flex flex-row justify-between my-8",
        children: [/*#__PURE__*/_jsx("div", {
          className: "flex items-center pt-2",
          "data-testid": "bread-crumbs",
          children: /*#__PURE__*/_jsx(Breadcrumb, {
            items: breadCrumbs
          })
        }), walletAddress && round && isSybilDefenseEnabled && /*#__PURE__*/_jsx("div", {
          "data-testid": "passport-widget",
          children: /*#__PURE__*/_jsx(PassportWidget, {
            round: round,
            alignment: "right"
          })
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: "mb-4 relative",
        children: [/*#__PURE__*/_jsx(ProjectBanner, {
          bannerImgCid: bannerImg !== null && bannerImg !== void 0 ? bannerImg : null,
          classNameOverride: "h-32 w-full object-cover lg:h-80 rounded md:rounded-3xl",
          resizeHeight: 320
        }), totalStaked && Number(totalStaked) > 0 && /*#__PURE__*/_jsx(StakedAmountCard, {
          totalStaked: Number(totalStaked)
        }), /*#__PURE__*/_jsx("div", {
          className: "pl-4 sm:pl-6 lg:pl-8",
          children: /*#__PURE__*/_jsx("div", {
            className: "sm:flex sm:items-end sm:space-x-5",
            children: /*#__PURE__*/_jsx("div", {
              className: "flex",
              children: /*#__PURE__*/_jsx(ProjectLogo, {
                ...(projectToRender === null || projectToRender === void 0 ? void 0 : projectToRender.projectMetadata)
              })
            })
          })
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: "md:flex gap-4 flex-row-reverse",
        children: [round && !isDirectRound(round) && /*#__PURE__*/_jsx(Sidebar, {
          isAlreadyInCart: isAlreadyInCart,
          isBeforeRoundEndDate: !disableAddToCartButton,
          removeFromCart: () => {
            remove(cartProject);
          },
          addToCart: () => {
            add(cartProject);
          }
        }), /*#__PURE__*/_jsx("div", {
          className: "flex-1",
          children: error === undefined && !isLoading && projectToRender !== undefined ? /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(Skeleton, {
              isLoaded: Boolean(title),
              children: /*#__PURE__*/_jsx("h1", {
                className: "text-4xl font-modern-era-medium tracking-tight text-grey-500",
                children: title
              })
            }), /*#__PURE__*/_jsx(ProjectLinks, {
              project: projectToRender
            }), /*#__PURE__*/_jsx(StakingBannerAndModal, {}), /*#__PURE__*/_jsx(ProjectDetailsTabs, {
              selected: selectedTab,
              onChange: handleTabChange,
              tabs: projectDetailsTabs.map(tab => tab.name)
            }), /*#__PURE__*/_jsx("div", {
              className: "[&_a]:underline",
              children: projectDetailsTabs[selectedTab].content
            })]
          }) : /*#__PURE__*/_jsx("p", {
            children: "Couldn't load project data. It may not exist."
          })
        })]
      })]
    })
  });
}
const StakedAmountCard = _ref => {
  let {
    totalStaked
  } = _ref;
  return /*#__PURE__*/_jsxs("div", {
    className: "p-2 bg-white/80 rounded-2xl backdrop-blur-sm inline-flex justify-start items-center gap-2 absolute top-4 right-4",
    children: [/*#__PURE__*/_jsx("div", {
      "data-svg-wrapper": true,
      className: "relative",
      children: /*#__PURE__*/_jsx("svg", {
        width: "20",
        height: "20",
        viewBox: "0 0 20 20",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        children: /*#__PURE__*/_jsx("path", {
          d: "M10.8334 8.33333V2.5L3.33337 11.6667H9.16671L9.16671 17.5L16.6667 8.33333L10.8334 8.33333Z",
          stroke: "#7D67EB",
          "stroke-width": "2",
          "stroke-linecap": "round",
          "stroke-linejoin": "round"
        })
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: "inline-flex flex-col justify-start items-start",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "self-stretch inline-flex justify-start items-center gap-1",
        children: [/*#__PURE__*/_jsx("div", {
          className: "justify-start text-text-primary text-lg font-medium font-['DM_Mono'] leading-normal",
          children: totalStaked
        }), /*#__PURE__*/_jsx("div", {
          className: "justify-start text-text-primary text-lg font-medium font-['DM_Mono'] leading-normal",
          children: "GTC"
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: "self-stretch justify-start text-text-primary text-sm font-normal font-['DM_Mono'] leading-[14px]",
        children: "Total staked"
      })]
    })]
  });
};